// Core types for noQuoteBooking application

export const BookingMode = {
  NO_QUOTE: 'no-quote',
  QUOTE: 'quote',
} as const;

export type BookingMode = typeof BookingMode[keyof typeof BookingMode]

// Контексты использования компонентов
export const ComponentContext = {
  BOOKING: 'booking',
  CABINET: 'cabinet',
} as const;

export type ComponentContext = typeof ComponentContext[keyof typeof ComponentContext]

// Типы персон для различения заявителя и гостя
export const PersonType = {
  APPLICANT: 'applicant',
  GUEST: 'guest',
} as const;

export type PersonType = typeof PersonType[keyof typeof PersonType]

// Режимы необходимости справки
export const CertRequirementMode = {
  REQUIRED: 'required',
  NOT_REQUIRED: 'not-required',
  QUERY: 'query',
} as const;

export type CertRequirementMode = typeof CertRequirementMode[keyof typeof CertRequirementMode]

export interface Selection {
  date: string
  month: number  // для with-quote режима (формат: год * 12 + месяц - 1)
  adults: number
  children: number
  skk: number
  sko: number
}

export interface UserPdn {
  id: number | null
  firstName: string
  secondName: string
  lastName: string
  sexId: number | null
  birthday: string
  snils: string
  benefitId: number | null
  relationId: number | null
  rankId: number | null
  rankCategoryId: number | null
  benefitAdditionalId: number | null
  additionalDoc: {
    id: number | null
    url: string
    name: string
    ext: string
    size: string
  }
  // Бэк говорит явно: доп. льгота есть, а скан к ней утерян
  additionalDocMissing: boolean
  benefitRelatedLabels: {
    relation: string
    rankCategory: string
  }
}

// Универсальный тип для справок (используется и для заявителя, и для гостей)
export interface Certificate {
  id: number | null
  diagnos: string
  certificateDate: string
  certificateNumber: string
  certificateEntity: string
  certScan: {
    id: string | number
    url: string
    name: string
    ext: string
    size: string
  }
}

// Алиас для обратной совместимости
export type UserCert = Certificate
export type GuestCert = Certificate

// Интерфейс для гостя
export interface Guest {
  id: number | null
  firstName: string
  secondName: string
  lastName: string
  sexId: number | null
  birthday: string
  snils: string
  benefitId: number | null
  relationId: number | null
  rankId: number | null
  rankCategoryId: number | null
  benefitAdditionalId: number | null
  additionalDoc: {
    id: number | null
    url: string
    name: string
    ext: string
    size: string
  }
  additionalDocMissing: boolean
  benefitRelatedLabels: {
    relation: string
    rankCategory: string
  }
}

export interface UserContacts {
  id: number | null
  country: string
  region: string
  city: string
  street: string
  house: string
  building: string
  flat: string
  phone: string
  email: string
}

export interface Lists {
  sex: Array<{ id: number; name: string }>
  benefit: Array<{ id: number; name: string }>
  rank: Array<{ id: number; name: string }>
  rankCategory: Array<{ id: number; name: string }>
  benefitAdditional: Array<{ id: number; name: string }>
  country: Array<{ value: number; label: string; phonePrefix: string; iso: string }>
}

export interface DocRules {
  enabledDoc: string[]
  maxFileSize: number
  messages: {
    required?: string
    fileSize: string
    fileType: string
  }
}

// API Response types
export interface ApiResponse<T = any> {
  status: string
  data?: T
  message?: string
}

export interface BenefitRelatedLabels {
  relation: string
  rankCategory: string
}

export interface CertRequirement {
  mode: CertRequirementMode
  message: string
}

// Field validator types
export interface ValidationRule {
  [key: string]: any
}

export interface FieldConfig {
  name: string
  validation: Record<string, ValidationRule | string>
}

export interface FieldSchema {
  [fieldKey: string]: FieldConfig
}

export interface ValidationErrors {
  [fieldKey: string]: string[]
}

export interface ValidationContext {
  http?: any
  mkbUrl?: string
  skoId?: number | string
  sko?: number | string
  skoID?: number | string
  bookDate?: string
  bookingDate?: string
  birthDate?: string
  benefitId?: number | null
  relationId?: number | null
  personType?: 'applicant' | 'guest'
  // Функция для серверной проверки кода МКБ
  checkMkb?: (params: { skoId: number | null; code?: string; diagnos?: string; bookDate?: string; bookingDate?: string; birthDate?: string; birthday?: string }) => Promise<any>
  // Функция для серверной проверки возраста
  checkAge?: (params: { skoId: number | null; benefitId: number | null; relationId: number | null; birthday?: string; birthDate?: string; bookingDate?: string; bookDate?: string; personType?: 'applicant' | 'guest' }) => Promise<any>
}

// Booking2 specific types
export interface SkkOption {
  id: number
  label: string
  skoOptions: SkoOption[]
}

export interface SkoOption {
  id: number
  label: string
}

export interface Booking2Config {
  maxAdults: number
  minAdults: number
  maxChildren: number
  minChildren: number
  skkOptions: SkkOption[]
}

export interface Booking2Form {
  date: string
  adults: number
  children: number
  skk: number
  sko: number
}

// Quote search types
export interface SkoFilial {
  id: number
  skkId: number
}

export interface Category {
  id: number
  name: string
  skoId: number
  skkId: number
}

export interface Quote {
  id: number
  categoryId: number
  from: string
  to: string
  skoId: number
  skkId: number
}

export interface SearchResults {
  categories: Category[]
  quotas: Quote[]
  messages: {
    filterDescription: string
    noteEmpty: string
  }
  showButtonOtherSko: boolean
  showButtonOtherPeriod: boolean
  sko: SkoFilial[]
}

export interface SearchParams {
  month: number | null
  adult: number
  children: number
  skk: number | null
  sko: number | null
}
